import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  alpha,
  Container,
  CircularProgress,
  useMediaQuery,
  Stack,
  Paper,
  Button,
} from "@mui/material";
import { MdSearch, MdClear, MdHistory, MdDownload } from "react-icons/md";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import debounce from "lodash.debounce";

// Medstown colors stored separately for clarity
const medstownColors = {
  primary: "#014D4D",
  secondary: "#02A0A0",
  accent: "#FFB800",
  success: "#4caf50",
  error: "#f44336",
  warning: "#ff9800",
  white: "#fff", // use hex always for alpha()
};

const SearchHistories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // States
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Fetch data from API
  const fetchData = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        "https://api.medstown.com/pharmacy/medicine/search/history",
        {
          params: { pincode: debouncedSearchText, limit: 100, page },
        }
      );
      const { list, totalPages } = res.data?.data || {};
      const rows = Array.isArray(list) ? list : [];
      // Ensure each row has a unique 'id' required by DataGrid
      const rowsWithId = rows.map((row, idx) => ({
        ...row,
        id: row._id || `temp-id-${idx}`,
      }));
      setData(rowsWithId);
      setFilteredRows(rowsWithId);
      setTotalPages(totalPages || 0);
      setCurrentPage(Math.min(page, totalPages || 1));
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText, currentPage]);

  const handleSearchTextChange = (event) => setSearchText(event.target.value);

  const clearSearch = () => {
    setSearchText("");
    setFilteredRows(data);
  };

  // Columns with Medstown color and compact fonts
  const columns = [
    {
      field: "search",
      headerName: "Search Term",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: medstownColors.primary }}
          noWrap
          title={params.value || "N/A"}
        >
          {params.value || "N/A"}
        </Typography>
      ),
    },
    {
      field: "resulted_products",
      headerName: "Result Count",
      flex: 0.5,
      minWidth: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value ?? "0"}
        </Typography>
      ),
    },
    {
      field: "user_id",
      headerName: "User ID",
      flex: 1.2,
      minWidth: 240,
      renderCell: (params) => {
        const userDocs = params?.row?.user_docs_id;
        if (!userDocs) return <Typography variant="body2">N/A</Typography>;
        return (
          <Box>
            <Typography variant="body2" component="div" fontWeight={700} noWrap>
              {userDocs.fullName || "N/A"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1 }}
              noWrap
            >
              {userDocs.phone || "N/A"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "pincode",
      headerName: "Pincode",
      flex: 0.8,
      minWidth: 120,
      valueGetter: (params) => params.row.pincode || "N/A",
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "searchedAt",
      headerName: "Created Date & Time",
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => {
        if (!params.row.searchedAt) return "N/A";
        const date = new Date(params.row.searchedAt);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
      },
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value}
        </Typography>
      ),
    },
  ];

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 2, sm: 3 },
        px: { xs: 1, sm: 3 },
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Paper
        sx={{
          mb: 3,
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${medstownColors.primary}, ${medstownColors.secondary})`,
          color: medstownColors.white,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 8px 24px rgba(1,77,77,0.3)",
        }}
        elevation={4}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <MdHistory size={36} />
          <Typography variant="h5" fontWeight={900} sx={{ letterSpacing: "-0.5px" }}>
            Search Histories
          </Typography>
        </Stack>

     
      </Paper>

      {/* Search input */}
      <TextField
        label="Search by Pincode or Phone Number"
        variant="outlined"
        size="small"
        fullWidth
        value={searchText}
        onChange={handleSearchTextChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MdSearch color={alpha(medstownColors.primary, 0.7)} />
            </InputAdornment>
          ),
          endAdornment: searchText ? (
            <InputAdornment position="end">
              <IconButton
                onClick={clearSearch}
                size="small"
                aria-label="Clear search"
              >
                <MdClear color={alpha(medstownColors.primary, 0.7)} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ mb: 2, fontSize: "0.85rem" }}
      />

      {/* Table with loading and empty states */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Paper
          elevation={1}
          sx={{
            flex: 1,
            borderRadius: 3,
            overflow: "hidden",
            "& .MuiDataGrid-root": {
              border: "none",
              fontSize: "0.85rem",
              backgroundColor: theme.palette.background.paper,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha(medstownColors.primary, 0.1),
              color: medstownColors.primary,
              fontWeight: 700,
              fontSize: "0.9rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha(medstownColors.secondary, 0.15),
              cursor: "pointer",
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                height: 400,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={36} color="primary" />
              <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                Loading data...
              </Typography>
            </Box>
          ) : data.length === 0 ? (
            <Box
              sx={{
                height: 400,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "text.secondary",
                px: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                No results found
              </Typography>
              <Typography variant="body2" mt={1}>
                Try adjusting your search criteria or clear the filter.
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pageSize={100}
              hideFooterPagination
              disableSelectionOnClick
              getRowId={(row) => row.id}
            />
          )}
        </Paper>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={3}
            mt={2}
          >
            <Button
              variant="contained"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              size="small"
              sx={{ minWidth: 90, fontSize: "0.85rem" }}
            >
              Previous
            </Button>
            <Typography fontWeight={600} fontSize="0.9rem">
              Page {currentPage} of {totalPages}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              size="small"
              sx={{ minWidth: 90, fontSize: "0.85rem" }}
            >
              Next
            </Button>
          </Stack>
        )}
      </Box>
    </Container>
  );
};

export default SearchHistories;
